import React, { useEffect, useRef } from 'react';
import {
  Button,
  parseDate,
  Typography,
  InputChangeEvent,
  Loading,
  Spacer,
  DefaultTheme,
} from '@guildeducationinc/recess';
import SuccessPictogram from '@guildeducationinc/recess/pictograms/SuccessPictogram';
import EducationPictogram from '@guildeducationinc/recess/pictograms/EducationPictogram';
import DeleteOutlineIcon from '@guildeducationinc/recess/icons/DeleteOutlineIcon';
import AddIcon from '@guildeducationinc/recess/icons/AddIcon';
import classnames from 'classnames';
import { useMediaQuery } from 'react-responsive';
import { FieldArray, Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '@guildeducationinc/guild-auth';
import { omit } from 'lodash';
import { ExecutionResult } from 'graphql';
import { DirtyProgramCompletion } from '@src/components/Pages/EditProfile/ProgramCompletion/ProgramCompletion';
import { clickable } from '@src/analytics';
import Divider from '@src/components/Shared/Divider/Divider';
import RouteLeavingGuard from '@src/components/Shared/RouteLeavingGuard/RouteLeavingGuard';
import { LARGE_QUERY } from '@src/constants';
import CheckboxInputField from '@src/components/Shared/FormInputFields/CheckboxInputField/CheckboxInputField';
import DatePickerField from '@src/components/Shared/FormInputFields/DatePickerField/DatePickerField';
import TextInputField from '@src/components/Shared/FormInputFields/TextInputField/TextInputField';
import useProgramCompletion from '@src/hooks/useProgramCompletion';
import useUserPrograms from '@src/hooks/useUserPrograms';
import AcademicPartnerSelectField, {
  DEFAULT_OPTION,
} from '@src/components/Pages/EditProfile/Fields/AcademicPartnerSelectField/AcademicPartnerSelectField';
import ProgramSelectField from '@src/components/Pages/EditProfile/Fields/ProgramSelectField/ProgramSelectField';
import { useConfirmation } from '@src/context/ConfirmationDialogContext';
import { useMemberPlanContext } from '@src/context/MemberPlanContext';
import CompletionDateField from '@pages/EditProfile/Fields/CompletionDateField/CompletionDateField';
import ProgramTypeSelectField from '@pages/EditProfile/Fields/ProgramTypeSelectField/ProgramTypeSelectField';
import CompletedProgram, { CompletedProgramData } from '../ProgramCompletion/CompletedProgram';
import { InProgressProgram } from '../ProgramCompletion/InProgressProgram';
import ProgramDeleteWarning from '../ProgramDialogs/ProgramDeleteWarning';
import ProgramSaveWarning from '../ProgramDialogs/ProgramSaveWarning';
import '../EditProfile.scss';

type Props = {
  setShowDeleteProgramError: React.Dispatch<React.SetStateAction<any>>;
  setShowError: React.Dispatch<React.SetStateAction<any>>;
  setShowMaxEnrollmentsError: React.Dispatch<React.SetStateAction<any>>;
  setShowSuccess: React.Dispatch<React.SetStateAction<any>>;
};

export const ACADEMIC_PARTNER_FIELD_LABEL = 'School or learning provider';
export const ACADEMIC_PARTNER_FIELD_LABEL_TEXT = 'School or learning provider name';
export const PROGRAM_FIELD_LABEL = 'Program name';
export const PROGRAM_FIELD_LABEL_TEXT = 'What is the name of your program?';
export const PROGRAM_TYPE_FIELD_LABEL = 'Program type';
export const EXPECTED_DATE_FIELD_LABEL = 'Expected completion date';
export const ADD_BUTTON_TEXT = 'Add program';
export const COMPLETED_CHECKBOX_LABEL = 'I have completed this program';
export const COMPLETION_DATE_FIELD_LABEL = 'Actual completion date';
export const CONGRATULATIONS_TEXT_PLURAL =
  'You did it! Congratulations on completing your programs.';
export const CONGRATULATIONS_TEXT_SINGLE =
  'You did it! Congratulations on completing your program.';
export const CONFIRM_SAVE_TITLE = 'Save completed program?';
export const DEFAULT_OPTION_VALUE = DEFAULT_OPTION.value;
export const DELETE_BUTTON_TEXT = 'Delete record';
export const NO_IN_PROGRESS_PROGRAMS_TEXT = 'Add a program, then provide enrollment details.';
export const NO_IN_PROGRESS_PROGRAMS_TITLE = 'No programs listed';
export const PROGRAM_COMPLETION_DATES_TITLE = 'Program completion dates';
export const PROGRAM_COMPLETION_DATES_SUBTITLE =
  'Tell us when you expect to complete any programs, even if they’re outside Guild’s catalog. Then update your record later with your actual completion dates.';
export const PROGRAM_DETAILS_TITLE = 'Program details';
export const SAVE_BUTTON_TEXT = 'Save changes';
export const DELETE_DIALOG_TITLE_TEXT = 'Delete record?';
export const DELETE_DIALOG_CONFIRMATION_TEXT = 'Delete';
export const MAX_COMPLETIONS = 5;

const validationSchema = Yup.object().shape({
  completions: Yup.array()
    .of(
      Yup.object().shape(
        {
          academicPartnerUuid: Yup.string().required(
            'Provide the name of the school or learning provider.'
          ),
          academicPartnerName: Yup.string().when('academicPartnerUuid', {
            is: DEFAULT_OPTION_VALUE,
            then: () =>
              Yup.string().required('Provide the name of the school or learning provider.'),
            otherwise: () => Yup.string(),
          }),
          programUuid: Yup.string()
            .ensure()
            .when('programName', {
              is: '',
              then: () => Yup.string().required('Provide the program name.'),
              otherwise: () => Yup.string(),
            })
            .when('academicPartnerUuid', {
              is: DEFAULT_OPTION_VALUE,
              then: () => Yup.string(),
              otherwise: () => Yup.string().required('Provide the program name.'),
            }),
          programName: Yup.string()
            .when('programUuid', {
              is: DEFAULT_OPTION_VALUE,
              then: () => Yup.string().required('Provide the program name.'),
              otherwise: () => Yup.string(),
            })
            .when('academicPartnerUuid', {
              is: DEFAULT_OPTION_VALUE,
              then: () => Yup.string().required('Provide the program name.'),
              otherwise: () => Yup.string(),
            }),
          programType: Yup.string().required('Provide the program type.'),
          expectedGraduationDate: Yup.string().required('Provide the expected completion date.'),
          actualGraduationDate: Yup.date()
            .nullable()
            .when('isCompleted', {
              is: true,
              then: () =>
                Yup.date()
                  .max(new Date(), 'The actual completion date must be today or in the past.')
                  .required('Provide the actual completion date.'),
            }),
        },
        [['programUuid', 'programName']]
      )
    )
    .min(1, 'No program details to save'),
});

export const getCompletedProgramData = ({
  academicPartnerName,
  programName,
  expectedGraduationDate,
  actualGraduationDate,
}: DirtyProgramCompletion): CompletedProgramData => {
  return {
    academicPartnerName,
    programName,
    expectedGraduationDate,
    actualGraduationDate,
  } as CompletedProgramData;
};

export const removeEmptyStrings = (completion: DirtyProgramCompletion) => {
  for (const key in completion) {
    if (completion[key] === '') {
      delete completion[key];
    }
  }
};

export const removeNotFoundUuids = (completion: DirtyProgramCompletion) => {
  if (completion.academicPartnerUuid === DEFAULT_OPTION_VALUE || !completion.academicPartnerUuid) {
    completion.academicPartnerUuid = '';
  }
  if (completion.programUuid === DEFAULT_OPTION_VALUE || !completion.programUuid) {
    completion.programUuid = '';
  }
};

export const parseDatesForMutation = (completion: DirtyProgramCompletion) => {
  const { expectedGraduationDate, actualGraduationDate } = completion;
  if (expectedGraduationDate) {
    completion.expectedGraduationDate = parseDate(expectedGraduationDate, 'MM-dd-yyyy')
      ?.toJSON()
      .split('T')[0] as string;
  }
  if (actualGraduationDate === null) {
    delete completion.actualGraduationDate;
  } else if (actualGraduationDate) {
    completion.actualGraduationDate = parseDate(actualGraduationDate, 'MM-dd-yyyy')
      ?.toJSON()
      .split('T')[0];
  }
  return completion;
};

const ProgramCompletionDates: React.FC<Props> = ({
  setShowDeleteProgramError,
  setShowError,
  setShowMaxEnrollmentsError,
  setShowSuccess,
}) => {
  const { user } = useAuth();
  const {
    deleteProgramCompletion,
    deleteProgramCompletionError,
    queryProgramCompletions,
    loadingProgramCompletions,
    createProgramCompletion,
    createProgramCompletionError,
    updateProgramCompletion,
    updateProgramCompletionError,
    sortedCompletedPrograms,
    sortedInProgressPrograms,
  } = useProgramCompletion();
  const isLargeWidth = useMediaQuery(LARGE_QUERY);
  const { programIds } = useMemberPlanContext();
  const { loading, programs } = useUserPrograms(programIds);
  const confirm = useConfirmation();
  const completedProgramsRef = useRef<HTMLElement>(null);

  const deleteButtonWrapperClassNames = classnames(
    isLargeWidth
      ? ['textAlign--center', 'margin--top_12']
      : ['position--absolute', 'deleteButtonWrapper']
  );

  const noInProgressProgramsClassNames = classnames(
    'noInProgressPrograms',
    'textAlign--center',
    'borderStyle--solid',
    'borderRadius--1',
    'borderWidth--1',
    'margin--bottom_48',
    'boxShadow--sm'
  );

  const programDetailsClassNames = classnames(
    'programDetails',
    'padding--x_24',
    'padding--bottom_24',
    'borderWidth--1',
    'borderColor--black-lighten-50',
    !isLargeWidth && 'position--relative'
  );

  const addProgramClickable = () => {
    clickable({
      loggedIn: true,
      product: 'account',
      tag: 'account_profile',
      text: 'add_program',
      type: 'button',
      location: 'program_completion_dates',
    });
  };

  const deleteRecordClickable = () => {
    clickable({
      loggedIn: true,
      product: 'account',
      tag: 'account_profile',
      text: 'delete_record',
      type: 'button',
      location: 'program_completion_dates',
    });
  };

  const confirmSaveClickable = () => {
    clickable({
      loggedIn: true,
      product: 'account',
      tag: 'account_profile_modal',
      text: 'save',
      type: 'button',
      location: 'program_completion_dates',
    });
  };

  const cancelSaveClickable = () => {
    clickable({
      loggedIn: true,
      product: 'account',
      tag: 'account_profile_modal',
      text: 'cancel',
      type: 'button',
      location: 'program_completion_dates',
    });
  };

  const checkboxClickable = () => {
    return clickable({
      product: 'account',
      loggedIn: true,
      tag: 'account_profile',
      text: 'program_completed',
      type: 'checkbox',
      location: 'program_completion_dates',
    });
  };

  useEffect(() => {
    let subscribed = true;

    if (subscribed && user?.id) {
      queryProgramCompletions();
    }

    return () => {
      subscribed = false;
    };
  }, [user, queryProgramCompletions]);

  useEffect(() => {
    let subscribed = true;

    if (subscribed && (createProgramCompletionError || updateProgramCompletionError)) {
      setShowError(true);
    }

    return () => {
      subscribed = false;
    };
  }, [createProgramCompletionError, setShowError, updateProgramCompletionError]);

  useEffect(() => {
    let subscribed = true;

    if (subscribed && deleteProgramCompletionError) {
      setShowDeleteProgramError(true);
    }

    return () => {
      subscribed = false;
    };
  }, [deleteProgramCompletionError, setShowDeleteProgramError]);

  const handleFormikSubmit = (
    { completions }: { completions: DirtyProgramCompletion[] },
    setSubmitting: Function,
    resetForm: Function
  ) => {
    if (sortedCompletedPrograms.length > 14) {
      setSubmitting(false);
      return setShowMaxEnrollmentsError(true);
    }

    const submitCompletions = () => {
      try {
        const mutationPromises = prepCompletionsForMutation(completions).map((completion) =>
          updateOrCreateCompletion(completion)
        );

        Promise.all<ExecutionResult>(mutationPromises).then((results) => {
          if (results.length) {
            queryProgramCompletions();
          }
        });
      } finally {
        resetForm();
        setShowSuccess(true);
        setSubmitting(false);
      }
    };

    const getCompletedCompletions = (completions: DirtyProgramCompletion[]) => {
      return completions.filter((completion) => {
        getProgramAndAPNames(completion);
        return completion.actualGraduationDate?.length;
      });
    };

    const completedCompletions = getCompletedCompletions(completions);

    if (completedCompletions.length) {
      confirm({
        catchOnCancel: true,
        title: CONFIRM_SAVE_TITLE,
        descriptionElement: <ProgramSaveWarning programs={completedCompletions} />,
        customOnSubmit: () => confirmSaveClickable(),
        customOnCancel: () => cancelSaveClickable(),
      })
        .then(() => {
          submitCompletions();
        })
        .catch(() => setSubmitting(false));
    } else {
      submitCompletions();
    }
  };

  const updateOrCreateCompletion = (completion: DirtyProgramCompletion) => {
    const { id, ...newData } = completion;
    if (id) {
      return updateProgramCompletion({
        variables: {
          completionId: id,
          completionData: newData,
        },
      });
    } else {
      return createProgramCompletion({
        variables: {
          completionData: {
            ...newData,
            studentUuid: user?.id,
          },
        },
      });
    }
  };

  const getChangedCompletions = (completions: DirtyProgramCompletion[]) => {
    return completions.filter((completion) => !isEmpty(completion) && hasChangedValues(completion));
  };

  const hasChangedValues = (newCompletion: DirtyProgramCompletion) => {
    const oldCompletion = sortedInProgressPrograms.find(
      (existingCompletion) => existingCompletion.id === newCompletion.id
    );
    if (!oldCompletion) {
      return true;
    }
    for (const key in newCompletion) {
      if (key !== 'isEditing' && newCompletion[key] !== oldCompletion[key]) {
        return true;
      }
    }
    return false;
  };

  const isEmpty = (completion: DirtyProgramCompletion) => {
    if (Object.keys(completion).length === 1 && completion.isEditing) {
      return true;
    }
    return false;
  };

  const prepCompletionsForMutation = (
    completions: DirtyProgramCompletion[]
  ): DirtyProgramCompletion[] => {
    return getChangedCompletions(
      completions.map((completion) => {
        parseDatesForMutation(completion);
        removeEmptyStrings(completion);
        removeNotFoundUuids(completion);
        getProgramAndAPNames(completion);
        return omit(completion, 'isCompleted', 'archived', 'studentUuid', 'isEditing');
      })
    );
  };

  const getProgramAndAPNames = (completion: DirtyProgramCompletion) => {
    if (completion.academicPartnerUuid && completion.academicPartnerUuid !== DEFAULT_OPTION_VALUE) {
      completion.academicPartnerName = programs.find(
        (program) => program.studentFacingAcademicPartner.uuid === completion.academicPartnerUuid
      )?.studentFacingAcademicPartner.name;
    }

    if (completion.programUuid && completion.programUuid !== DEFAULT_OPTION_VALUE) {
      completion.programName = programs.find(
        (program) => program.uuid === completion.programUuid
      )?.name;
    }
  };

  const getDeleteRecordLabel = (completion: DirtyProgramCompletion, index: number) => {
    return !completion.programName || !completion.academicPartnerName
      ? `Delete incomplete record number ${index + 1}`
      : `Delete the record for ${completion.programName} at ${completion.academicPartnerName}`;
  };

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          completions: sortedInProgressPrograms.length
            ? sortedInProgressPrograms
            : [{ isEditing: true }],
        }}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          clickable({
            product: 'account',
            loggedIn: true,
            tag: 'account_profile',
            text: 'save_changes',
            type: 'button',
            location: 'program_completion_dates',
          });
          handleFormikSubmit(values, setSubmitting, resetForm);
        }}
        validationSchema={validationSchema}
      >
        {({
          dirty,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          isValid,
          setFieldValue,
          values,
        }) => (
          <>
            <RouteLeavingGuard when={getChangedCompletions(values.completions).length > 0} />
            <Loading
              label='edit profile'
              loaded={!isSubmitting && !loadingProgramCompletions}
              showChildrenWhileLoading={true}
              overlay={true}
            >
              <Typography variant='h3'>{PROGRAM_COMPLETION_DATES_TITLE}</Typography>
              <Typography variant='body1' className='padding--y_8'>
                * Required
              </Typography>
              <Typography variant='body1' className='padding--bottom_32 accountSectionDescription'>
                {PROGRAM_COMPLETION_DATES_SUBTITLE}
              </Typography>
              <Form>
                <FieldArray
                  name='completions'
                  render={(arrayHelpers) => (
                    <>
                      {values?.completions?.map(
                        (completion: DirtyProgramCompletion, index: number) => (
                          <fieldset
                            className={classnames([
                              programDetailsClassNames,
                              index > 0 && 'margin--top_64',
                            ])}
                            key={
                              /* eslint-disable-next-line react/no-array-index-key */
                              index
                            }
                          >
                            <legend className='programDetailsHeading'>
                              <Typography
                                variant='h4'
                                className='headingBackground padding--bottom_24 padding--x_8 display--inlineBlock'
                              >
                                {PROGRAM_DETAILS_TITLE}
                              </Typography>
                            </legend>
                            {!completion?.isEditing && (
                              <InProgressProgram
                                handleEdit={() =>
                                  arrayHelpers.replace(index, {
                                    ...completion,
                                    isEditing: true,
                                  })
                                }
                                programData={completion}
                              />
                            )}
                            <div
                              className={classnames([
                                'accountFormFields',
                                !completion?.isEditing && 'display--none',
                              ])}
                            >
                              <Spacer pb={24}>
                                <AcademicPartnerSelectField
                                  completionIndex={index}
                                  helpText='Type the full name of the school or "Not found" if it is not listed.'
                                  label={ACADEMIC_PARTNER_FIELD_LABEL}
                                  loading={loading}
                                  name={`completions[${index}].academicPartnerUuid`}
                                  programs={programs}
                                />
                              </Spacer>
                              {completion?.academicPartnerUuid === DEFAULT_OPTION_VALUE && (
                                <Spacer pb={24}>
                                  <TextInputField
                                    helpText='Type the full name of the school or learning provider.'
                                    label={ACADEMIC_PARTNER_FIELD_LABEL_TEXT}
                                    name={`completions[${index}].academicPartnerName`}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    required={true}
                                    value={completion?.academicPartnerName ?? ''}
                                  />
                                </Spacer>
                              )}
                              {completion?.academicPartnerUuid !== DEFAULT_OPTION_VALUE && (
                                <Spacer pb={24}>
                                  <ProgramSelectField
                                    completionIndex={index}
                                    helpText='Type "Not found" if your program is not listed.'
                                    label={PROGRAM_FIELD_LABEL}
                                    loading={loading}
                                    name={`completions[${index}].programUuid`}
                                    programs={programs}
                                    selectedAcademicPartnerUuid={completion?.academicPartnerUuid}
                                  />
                                </Spacer>
                              )}
                              {(completion?.academicPartnerUuid === DEFAULT_OPTION_VALUE ||
                                completion?.programUuid === DEFAULT_OPTION_VALUE) && (
                                <Spacer pb={24}>
                                  <TextInputField
                                    label={
                                      completion?.academicPartnerUuid === DEFAULT_OPTION_VALUE
                                        ? PROGRAM_FIELD_LABEL
                                        : PROGRAM_FIELD_LABEL_TEXT
                                    }
                                    name={`completions[${index}].programName`}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    required={true}
                                    value={completion?.programName ?? ''}
                                  />
                                </Spacer>
                              )}
                              <Spacer pb={24}>
                                <ProgramTypeSelectField
                                  label={PROGRAM_TYPE_FIELD_LABEL}
                                  name={`completions[${index}].programType`}
                                />
                              </Spacer>
                              <Spacer pb={24}>
                                <DatePickerField
                                  label={EXPECTED_DATE_FIELD_LABEL}
                                  id='expectedGraduationDate'
                                  helpText='Use your learning provider&rsquo;s student portal or website for guidance. Provide your best guess if you&rsquo;re still unsure.'
                                  name={`completions[${index}].expectedGraduationDate`}
                                  required={true}
                                  value={completion?.expectedGraduationDate as string}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                              </Spacer>
                            </div>
                            <CheckboxInputField
                              name={`completions[${index}].isCompleted`}
                              label={COMPLETED_CHECKBOX_LABEL}
                              checked={!!completion?.isCompleted}
                              value='completed'
                              onChange={(e: InputChangeEvent) => {
                                setFieldValue(`completions[${index}].actualGraduationDate`, '');
                                checkboxClickable();
                              }}
                              onBlur={handleBlur}
                            />
                            <div
                              className={
                                completion?.isEditing
                                  ? deleteButtonWrapperClassNames
                                  : 'display--none'
                              }
                            >
                              <Button
                                appearance='ghost'
                                iconDirection='left'
                                icon={<DeleteOutlineIcon size={24} />}
                                intent='danger'
                                label={getDeleteRecordLabel(completion, index)}
                                onClick={() => {
                                  deleteRecordClickable();
                                  if (completion?.id) {
                                    confirm({
                                      title: DELETE_DIALOG_TITLE_TEXT,
                                      confirmButtonText: DELETE_DIALOG_CONFIRMATION_TEXT,
                                      confirmButtonIntent: 'danger',
                                      descriptionElement: <ProgramDeleteWarning {...completion} />,
                                      customOnSubmit: () => {
                                        clickable({
                                          loggedIn: true,
                                          product: 'account',
                                          tag: 'account_profile',
                                          text: 'confirm_delete_record',
                                          type: 'button',
                                        });
                                      },
                                      customOnCancel: () => {
                                        clickable({
                                          loggedIn: true,
                                          product: 'account',
                                          tag: 'account_profile',
                                          text: 'cancel_delete_record',
                                          type: 'button',
                                        });
                                      },
                                    }).then(() => {
                                      deleteProgramCompletion({
                                        variables: { completionId: completion.id! },
                                      }).then(({ data }) => {
                                        if (data?.deleteCompletion?.deleted) {
                                          arrayHelpers.remove(index);
                                        }
                                      });
                                    });
                                  } else {
                                    arrayHelpers.remove(index);
                                  }
                                }}
                              >
                                {DELETE_BUTTON_TEXT}
                              </Button>
                            </div>
                            {completion.isCompleted && (
                              <CompletionDateField
                                label={COMPLETION_DATE_FIELD_LABEL}
                                id={completion?.id as string}
                                name={`completions[${index}].actualGraduationDate`}
                                value={completion?.actualGraduationDate as string}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            )}
                          </fieldset>
                        )
                      )}
                      {values?.completions?.length < MAX_COMPLETIONS &&
                        values?.completions?.length > 0 && (
                          <div className='flex flexJustify--end padding--bottom_48'>
                            <Button
                              theme='light'
                              appearance='ghost'
                              iconDirection='left'
                              icon={<AddIcon size={24} />}
                              onClick={() => {
                                arrayHelpers.insert(values?.completions?.length || 0, {
                                  isEditing: true,
                                });
                                addProgramClickable();
                              }}
                            >
                              {ADD_BUTTON_TEXT}
                            </Button>
                          </div>
                        )}
                      {!values?.completions.length && (
                        <div className={noInProgressProgramsClassNames}>
                          <div className='flex flexJustify--center padding--y_20'>
                            <EducationPictogram size={64} strokeColor={DefaultTheme.iconColor} />
                          </div>
                          <h2 className='text-black-base padding--0 margin--0'>
                            {NO_IN_PROGRESS_PROGRAMS_TITLE}
                          </h2>
                          <p className='padding--0 margin--0'>{NO_IN_PROGRESS_PROGRAMS_TEXT}</p>
                          <div className='flex flexJustify--center padding--bottom_40 padding--top_24'>
                            <Button
                              iconDirection='left'
                              icon={<AddIcon size={24} />}
                              onClick={() => {
                                arrayHelpers.insert(0, {
                                  isEditing: true,
                                });
                                addProgramClickable();
                              }}
                            >
                              {ADD_BUTTON_TEXT}
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                />
                <Divider color='black-lighten-70' />
                <div className='flex flexJustify--end padding--top_24'>
                  <Button
                    type='button'
                    onClick={() => {
                      handleSubmit();
                    }}
                    disabled={!dirty || !isValid || isSubmitting}
                    testid='account-save-button'
                  >
                    {SAVE_BUTTON_TEXT}
                  </Button>
                </div>
              </Form>
            </Loading>
          </>
        )}
      </Formik>
      {sortedCompletedPrograms.length > 0 && (
        <section ref={completedProgramsRef} className='completedProgramSection margin--top_24'>
          <Typography variant='h4'>Completed programs</Typography>
          <div className='completedProgramsSubheadingGroup flex flexItems--center margin--top_8 margin--bottom_24'>
            <SuccessPictogram strokeColor={DefaultTheme.iconColor} />
            <Typography variant='body1' className='completedProgramsSubheadingText margin--left_16'>
              {sortedCompletedPrograms.length > 1
                ? CONGRATULATIONS_TEXT_PLURAL
                : CONGRATULATIONS_TEXT_SINGLE}
            </Typography>
          </div>
          {sortedCompletedPrograms.map((completedProgram) => (
            <CompletedProgram
              key={completedProgram?.id}
              programData={getCompletedProgramData(completedProgram)}
            />
          ))}
        </section>
      )}
    </>
  );
};

export default ProgramCompletionDates;
